
import React from 'react';
import { Card } from "@/components/ui/card";
import { Timer } from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ZAxis, Legend } from 'recharts';
import type { PerformanceData } from './types';

interface SpeedAccuracyChartProps {
  performanceData: PerformanceData[];
}

export const SpeedAccuracyChart: React.FC<SpeedAccuracyChartProps> = ({ performanceData }) => {
  const speedVsAccuracy = React.useMemo(() => 
    performanceData.map(data => ({
      timePerQuestion: (data.total_questions ? data.total_questions * 2 : 0),
      accuracy: (data.correct_answers / data.total_questions) * 100,
      subject: data.subject,
      difficulty: data.difficulty
    })), 
    [performanceData]
  );

  // Group data by difficulty level
  const groupedData = React.useMemo(() => {
    const grouped = {
      easy: speedVsAccuracy.filter(item => item.difficulty === 'easy'),
      medium: speedVsAccuracy.filter(item => item.difficulty === 'medium'),
      hard: speedVsAccuracy.filter(item => item.difficulty === 'hard')
    };
    return grouped;
  }, [speedVsAccuracy]);

  return (
    <Card className="p-6 bg-gradient-to-br from-blue-50 to-white border border-slate-100 shadow-sm">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-blue-500">
        <Timer className="w-5 h-5 text-blue-500" />
        Speed vs Accuracy Trade-off
      </h2>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 10 }}>
            <defs>
              <filter id="glow" height="300%" width="300%" x="-100%" y="-100%">
                <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis 
              dataKey="timePerQuestion" 
              name="Time"
              label={{ 
                value: 'Time per Question (seconds)', 
                position: 'bottom',
                style: { fill: '#6B7280' } 
              }}
              tick={{ fill: '#6B7280' }}
              type="number"
              domain={[0, 'dataMax + 5']}
            />
            <YAxis 
              dataKey="accuracy" 
              name="Accuracy"
              label={{ 
                value: 'Accuracy (%)', 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: '#6B7280' }
              }}
              tick={{ fill: '#6B7280' }}
              domain={[0, 100]}
            />
            <ZAxis range={[60, 400]} />
            <Tooltip 
              cursor={{ strokeDasharray: '3 3' }}
              contentStyle={{ 
                backgroundColor: 'white', 
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #E5E7EB'
              }}
              formatter={(value: number, name: string) => {
                if (name === 'Accuracy') return [`${value.toFixed(1)}%`, name];
                if (name === 'Time') return [`${value.toFixed(1)} seconds`, name];
                return [value, name];
              }}
              labelFormatter={(value) => `Question Time vs Accuracy`}
            />
            <Legend 
              verticalAlign="top" 
              height={36}
              formatter={(value) => {
                const colors = {
                  easy: 'text-green-600',
                  medium: 'text-amber-600',
                  hard: 'text-red-600'
                };
                return <span className={`text-sm ${colors[value as keyof typeof colors]} font-medium`}>{value.toUpperCase()}</span>;
              }}
            />
            
            <Scatter 
              name="easy" 
              data={groupedData.easy} 
              fill="#4ADE80"
              fillOpacity={0.7}
              stroke="#22C55E"
              strokeWidth={1}
              filter="url(#glow)"
            />
            <Scatter 
              name="medium" 
              data={groupedData.medium}
              fill="#F59E0B"
              fillOpacity={0.7} 
              stroke="#D97706"
              strokeWidth={1}
              filter="url(#glow)"
            />
            <Scatter 
              name="hard" 
              data={groupedData.hard}
              fill="#EF4444" 
              fillOpacity={0.7}
              stroke="#DC2626"
              strokeWidth={1}
              filter="url(#glow)"
            />
            
            <ReferenceLine
              y={80}
              stroke="#16A34A"
              strokeDasharray="8 4"
              label={{
                value: "Target Accuracy",
                position: "insideBottomRight",
                style: { fill: "#16A34A", fontSize: 12 }
              }}
            />
            
            {/* Optimal zone - balancing speed and accuracy */}
            <ReferenceLine
              x={25}
              stroke="#8B5CF6"
              strokeDasharray="8 4"
              label={{
                value: "Optimal Time",
                position: "insideTopRight",
                style: { fill: "#8B5CF6", fontSize: 12 }
              }}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 text-sm text-gray-600">
        <div className="flex items-center gap-1.5 justify-center">
          <div className="w-3 h-3 rounded-full bg-purple-500 opacity-50"></div>
          <span>Optimal learning happens when balancing speed and accuracy</span>
        </div>
      </div>
    </Card>
  );
};
